import { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface GridProps extends ComponentProps<'div'> {
  children: ReactNode
  className?: string
}

/**
 * Grid Container Component
 * 
 * Sistema de grid responsivo da Tãmi:
 * - Mobile: 4 colunas (< 768px)
 * - Tablet: 8 colunas (>= 768px)
 * - Desktop: 12 colunas (>= 1024px)
 * 
 * @example
 * ```tsx
 * <Grid>
 *   <div className="col-m-4 col-t-6 col-d-8">Conteúdo</div>
 * </Grid>
 * ```
 */
export function Grid({ children, className, ...props }: GridProps) {
  return (
    <div 
      className={cn('grid-container py-8 px-4 md:py-16 md:px-8', className)} 
      {...props}
    >
      {children}
    </div>
  )
}

interface GridItemProps extends ComponentProps<'div'> {
  children: ReactNode
  className?: string
  /** Colunas no mobile (1-4) */
  mobile?: 1 | 2 | 3 | 4 | 'full'
  /** Colunas no tablet (1-8) */
  tablet?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 'full'
  /** Colunas no desktop (1-12) */
  desktop?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'full'
}

/**
 * Grid Item Component
 * 
 * Facilita a definição de colunas responsivas via props
 * 
 * @example
 * ```tsx
 * <Grid>
 *   <GridItem mobile={4} tablet={6} desktop={8}>
 *     Conteúdo responsivo
 *   </GridItem>
 * </Grid>
 * ```
 */
export function GridItem({ 
  children, 
  className, 
  mobile, 
  tablet, 
  desktop,
  ...props 
}: GridItemProps) {
  const classes = cn(
    mobile && `col-m-${mobile}`,
    tablet && `col-t-${tablet}`,
    desktop && `col-d-${desktop}`,
    className
  )

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  )
}

// Export como namespace para uso mais limpo
export const GridSystem = {
  Container: Grid,
  Item: GridItem,
}

